import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useToast } from "@hightouch/htui";
import pLimit from "p-limit";
import {
  areBrandDisclaimerDefaultsDisabled,
  type BrandDisclaimerFont,
} from "@hightouch/lib/ad-studio/disclaimer/brand-defaults";
import {
  DISABLED_DISCLAIMER_SPEC,
  type DisclaimerSpec,
} from "@hightouch/lib/ad-studio/disclaimer/types";
import { type AdBriefAssetQuery, useAdBriefAssetQuery } from "src/graphql";
import {
  addAiDisclosureMetadataToPng,
  applyVisibleDisclaimer,
} from "src/pages/ad-studio/html-ad-editor/apply-disclaimer";
import {
  buildHtml5BundleFromPng,
  convertPngToJpeg,
  HTML5_BUNDLE_MAX_ZIPPED_BYTES,
} from "src/pages/ad-studio/html-ad-editor/build-html5-bundle";
import { captureContent } from "src/pages/ad-studio/html-ad-editor/capture-content";
import { hostedFontStylesheetUrls } from "src/pages/ad-studio/html-ad-editor/hosted-font-stylesheet-urls";
import { buildPsdBlob } from "src/pages/ad-studio/psd-export/build-psd";
import { canBuildLayeredExport } from "src/pages/ad-studio/layered-export/prepare-layered-export";
import { buildIdmlBlob } from "src/pages/ad-studio/idml-export/build-idml";
import type { PsdFontResolver } from "@hightouch/lib/ad-studio/psd-export/psd-font";
import { usePsdFontResolver } from "src/pages/ad-studio/psd-export/use-psd-font-resolver";
import type {
  DisclaimerDownloadResult,
  DisclaimerPreviewItem,
  ExportSettingsResult,
  VideoExportSettings,
} from "src/pages/ad-studio/disclaimer/export-settings";
import { adDisplayName } from "src/pages/ad-studio/components/ad-display-name";
import { getAdAssetKind } from "src/pages/ad-studio/components/ad-asset-kind";
import { isVideoAsset } from "src/pages/ad-studio/briefs/utils";
import {
  DownloadDialog,
  type DownloadDialogSettings,
} from "src/pages/ad-studio/components/download-dialog";
import { useDownloadProgress } from "src/pages/ad-studio/hooks/use-download-progress";
import {
  partitionAssetsForDownload,
  selectVideoExportTargets,
} from "src/pages/ad-studio/briefs/hooks/bulk-video-download-targets";
import {
  type AdAssetDownloadFormat,
  DEFAULT_PDF_IMAGE_FILE_TYPE,
  type DownloadOption,
  type ImageFileType,
  MAX_PDF_EXPORT_ADS,
  PNG_IMAGE_FILE_TYPE,
  downloadOptionsToFormats,
} from "src/pages/ad-studio/briefs/components/download-options";
import {
  type ReviewPdfLabelColors,
  type ReviewPdfPage,
  buildReviewPdf,
} from "src/pages/ad-studio/pdf-export/build-review-pdf";
import { useReviewPdfLabelColors } from "src/pages/ad-studio/pdf-export/use-review-pdf-label-colors";

export type { AdAssetDownloadFormat } from "src/pages/ad-studio/briefs/components/download-options";
import { useFontInjection } from "src/pages/ad-studio/hooks/use-font-injection";
import {
  type RasterizeAdSubject,
  type RasterizeAdTarget,
  assetVersionRasterTarget,
  requestAdRaster,
} from "src/pages/ad-studio/ad-raster";
import { useBrandDisclaimerDefaultsResolver } from "src/pages/ad-studio/disclaimer/use-brand-disclaimer-defaults";
import {
  type ResizeFields,
  sizeAssetResizesOf,
} from "src/pages/ad-studio/resizes-of";
import { useRegionConfig } from "src/utils/use-region-config";
import {
  downloadBlobsAsZipOrSingle,
  fetchUrlAsBlob,
  resolveFilename,
  sanitizeFilename,
} from "src/utils/download";
import { captureException } from "src/lib/monitoring";

export type DownloadedAdAsset = NonNullable<
  AdBriefAssetQuery["ad_brief_assets_by_pk"]
>;

export type ZipEntry = { filename: string; blob: Blob };

/** Other files to put in the same archive as the ads, under the caller's archive name. */
export type ExtraZipEntries = { entries: ZipEntry[]; zipName: string };

export type AdAssetDownloadEntriesResult = {
  entries: ZipEntry[];
  /** Notices to surface: an oversized HTML5 bundle, a PSD that failed. */
  warnings: string[];
  /** Rendered images for the export's one review PDF; empty unless "pdf" was requested. */
  pdfPages: ReviewPdfPage[];
};

type BuildAdAssetDownloadEntriesArgs = {
  asset: Pick<
    DownloadedAdAsset,
    "id" | "asset_name" | "asset_type" | "asset_url" | "svg" | "version_id"
  > & {
    component_assets?: unknown;
    video_composition?: unknown;
    ad_brief_asset_size_variants?:
      | DownloadedAdAsset["ad_brief_asset_size_variants"]
      | null;
    ad_brief?: Pick<
      NonNullable<DownloadedAdAsset["ad_brief"]>,
      "resizes"
    > | null;
  };
  spec: DisclaimerSpec;
  font?: BrandDisclaimerFont;
  getFontRulesForMarkup: (markup: string) => Promise<string[]>;
  /**
   * PSD-only: names text layers after the brand font, not the upload alias. A thunk so the font
   * index is fetched only when a PSD is actually built — its failure must not sink a PNG export.
   */
  loadFontResolver?: () => Promise<PsdFontResolver>;
  apiBase: string;
  formats?: AdAssetDownloadFormat[];
  /** Encoding for the "png" format's image file. */
  imageFileType?: ImageFileType;
  /** Defaults to `<asset name>-primary`; overridden for an edited canvas or size view. */
  primaryEntryName?: string;
  /** Row the primary render is fingerprinted as; null when no stored row backs it. */
  primaryRasterTarget: RasterizeAdTarget | null;
};

const PARALLEL_AD_DOWNLOADS = 4;
const PARALLEL_RECORD_FETCHES = 8;
const SIZE_LABEL_COLLATOR = new Intl.Collator("en", { numeric: true });

const SINGLE_FORMAT_VIDEOS_NOTICE = {
  title: "Videos aren't included",
  message: "This export holds only image ads.",
};
const BULK_FORMAT_OPTIONS: DownloadOption[] = [
  "images",
  "pdf",
  "html5",
  "psd",
  "idml",
];

export const PDF_EXPORT_CAP_TOAST = {
  id: "pdf-export-over-cap",
  title: `Your export includes the first ${MAX_PDF_EXPORT_ADS} ads`,
  message: `An export with a PDF can hold up to ${MAX_PDF_EXPORT_ADS} ads. To export the rest, select fewer ads.`,
  variant: "warning",
} as const;

/** Channel, then size, then id: the query orders sizes by a timestamp that batch-created sizes share. */
function sortResizesForExport<T extends ResizeFields>(resizes: T[]): T[] {
  return [...resizes].sort(
    (a, b) =>
      SIZE_LABEL_COLLATOR.compare(
        a.channel_size?.channel?.name ?? "",
        b.channel_size?.channel?.name ?? "",
      ) ||
      SIZE_LABEL_COLLATOR.compare(
        a.channel_size?.label ?? "",
        b.channel_size?.label ?? "",
      ) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** Keeps an export that includes a PDF to the first `MAX_PDF_EXPORT_ADS` items, for every format; other exports pass through. */
export function capPdfExport<T>(
  items: T[],
  formats: AdAssetDownloadFormat[],
): { items: T[]; isOverCap: boolean } {
  return formats.includes("pdf") && items.length > MAX_PDF_EXPORT_ADS
    ? { items: items.slice(0, MAX_PDF_EXPORT_ADS), isOverCap: true }
    : { items, isOverCap: false };
}

/** Builds the export's one review PDF from every ad's pages, in the order given. */
export async function buildReviewPdfEntry({
  pages,
  imageFileType,
  labelColors,
  filename,
}: {
  pages: ReviewPdfPage[];
  imageFileType: ImageFileType;
  labelColors: ReviewPdfLabelColors;
  /** Without the extension. */
  filename: string;
}): Promise<{ entry: ZipEntry | null; warnings: string[] }> {
  if (pages.length === 0) return { entry: null, warnings: [] };
  const { blob, skipped } = await buildReviewPdf({
    pages,
    imageFileType,
    labelColors,
  });
  const warnings = skipped.map(
    (name) => `${name} couldn't be added to the PDF.`,
  );
  if (!blob) {
    warnings.push("The PDF couldn't be created. Try exporting it again.");
    return { entry: null, warnings };
  }
  return {
    entry: { filename: `${sanitizeFilename(filename)}.pdf`, blob },
    warnings,
  };
}

/** A layered build holds a full-size and 2x canvas per layer, so those go one ad at a time. */
export function adDownloadConcurrency(
  formats: AdAssetDownloadFormat[],
): number {
  return formats.some((format) => format === "psd" || format === "idml")
    ? 1
    : PARALLEL_AD_DOWNLOADS;
}

/**
 * Builds the downloadable files for one ad asset: SVG-backed ads render to the requested
 * formats, URL-only assets download their stored file as-is. Individual failures are
 * logged and skipped so one bad variant doesn't sink the download.
 */
export async function buildAdAssetDownloadEntries({
  asset,
  spec,
  font,
  getFontRulesForMarkup,
  loadFontResolver,
  apiBase,
  formats = ["png"],
  imageFileType = PNG_IMAGE_FILE_TYPE,
  primaryEntryName,
  primaryRasterTarget,
}: BuildAdAssetDownloadEntriesArgs): Promise<AdAssetDownloadEntriesResult> {
  const baseName = asset.asset_name || "asset";
  const primaryComponentAssets =
    (asset.component_assets as Record<string, string> | null) ?? null;

  const renderPng = async (
    disclosed: string,
    fontRules: string[],
    componentAssets: Record<string, string> | null,
  ): Promise<Blob> => {
    const blob = await captureContent({
      assetId: asset.id,
      content: disclosed,
      fontRules,
      componentAssets,
      apiBase,
    });
    return spec.machineReadable ? addAiDisclosureMetadataToPng(blob) : blob;
  };

  const entries: ZipEntry[] = [];
  const warnings: string[] = [];
  const pdfPages: ReviewPdfPage[] = [];
  const displayName = adDisplayName({ assetName: asset.asset_name });

  // Reached when the shared work (disclaiming, font rules) fails, so per-format warnings
  // never ran. Silence here would surface as "nothing to download".
  const pushPrepFailure = (entryName: string, err: unknown): void => {
    console.error(`asset-download: ${entryName} preparation failed`, err);
    warnings.push(
      `${entryName} couldn't be prepared, so its files were skipped.`,
    );
  };

  // Only PNG and its HTML5 wrapper rasterize; layered files build from the markup directly.
  // Each exported row is also rasterized and fingerprinted server-side, regardless
  // of format, for matching against ads launched on a platform.
  const pushRenderedEntries = async ({
    content,
    entryName,
    raster,
    componentAssets,
  }: {
    content: string;
    entryName: string;
    raster: RasterizeAdSubject | null;
    componentAssets: Record<string, string> | null;
  }): Promise<void> => {
    const disclosed = spec.visible
      ? applyVisibleDisclaimer(content, spec, font)
      : content;
    const fontRules = await getFontRulesForMarkup(disclosed);

    if (raster) {
      void requestAdRaster({ apiEndpoint: apiBase, ...raster });
    }

    // Isolated from the layered exports below: either side failing is a warning, not the loss of the other.
    if (
      formats.includes("png") ||
      formats.includes("html5") ||
      formats.includes("pdf")
    ) {
      try {
        const blob = await renderPng(disclosed, fontRules, componentAssets);
        if (formats.includes("pdf")) {
          pdfPages.push({
            name: entryName,
            label: displayName,
            image: blob,
            machineReadableDisclosure: spec.machineReadable,
          });
        }
        if (formats.includes("png")) {
          entries.push(
            imageFileType.type === "jpg"
              ? {
                  filename: `${sanitizeFilename(entryName)}.jpg`,
                  blob: await convertPngToJpeg(blob, {
                    quality: imageFileType.quality,
                    machineReadableDisclosure: spec.machineReadable,
                  }),
                }
              : { filename: `${sanitizeFilename(entryName)}.png`, blob },
          );
        }
        if (formats.includes("html5")) {
          const bundle = await buildHtml5BundleFromPng(blob, {
            machineReadableDisclosure: spec.machineReadable,
          });
          const filename = `${sanitizeFilename(`${entryName}-html5`)}.zip`;
          entries.push({ filename, blob: bundle.blob });
          if (bundle.overBudget) {
            warnings.push(
              `${filename} is ${Math.ceil(bundle.zippedBytes / 1024)} KB zipped, over the ${Math.floor(HTML5_BUNDLE_MAX_ZIPPED_BYTES / 1024)} KB limit Google Ads enforces on HTML5 creatives — the channel may reject it.`,
            );
          }
        }
      } catch (err) {
        console.error(`asset-download: ${entryName} render failed`, err);
        warnings.push(
          `${entryName} couldn't be rendered, so its image files were skipped.`,
        );
      }
    }

    for (const format of formats.filter(
      (format) => format === "psd" || format === "idml",
    )) {
      const filename = `${sanitizeFilename(entryName)}.${format}`;
      if (hostedFontStylesheetUrls(fontRules).length > 0) {
        warnings.push(
          `${filename} couldn't be generated because Adobe Fonts aren't supported in Photoshop or InDesign exports. Download an image instead.`,
        );
        continue;
      }
      if (!canBuildLayeredExport(content)) {
        // The dialog hides layered formats when no selected ad can produce one, so this is the mixed case.
        warnings.push(
          `${filename} couldn't be generated — this ad's markup can't be exported as layers.`,
        );
        continue;
      }
      try {
        // Undisclosed markup plus the applier, so the builder exports the disclosure as its
        // own top layer — its bottom-anchored auto-sized geometry doesn't survive the round-trip.
        const buildLayeredBlob =
          format === "psd" ? buildPsdBlob : buildIdmlBlob;
        const blob = await buildLayeredBlob({
          content,
          fontRules,
          componentAssets,
          apiBase,
          resolveFont: await loadFontResolver?.(),
          machineReadableDisclosure: spec.machineReadable,
          applyVisibleDisclaimer: spec.visible
            ? (markup) => applyVisibleDisclaimer(markup, spec, font)
            : undefined,
        });
        entries.push({ filename, blob });
      } catch (err) {
        console.error(`asset-download: ${filename} layered export failed`, err);
        // For a single-format request this warning is the whole outcome.
        warnings.push(
          formats.some((other) => other !== format)
            ? `${filename} couldn't be generated — the ad's other formats still downloaded.`
            : `${filename} couldn't be generated. Try downloading it again, or export a PNG instead.`,
        );
      }
    }
  };

  if (asset.svg) {
    const primaryName = primaryEntryName ?? `${baseName}-primary`;
    try {
      await pushRenderedEntries({
        content: asset.svg,
        entryName: primaryName,
        raster: primaryRasterTarget
          ? { assetId: asset.id, target: primaryRasterTarget }
          : null,
        componentAssets: primaryComponentAssets,
      });
    } catch (err) {
      pushPrepFailure(primaryName, err);
    }

    // Every resize with markup is exported; the zip layer disambiguates clashing names.
    for (const resize of sortResizesForExport(sizeAssetResizesOf(asset))) {
      if (!resize.svg) continue;
      // Two channels can share a size label ("1:1"), so include the channel name.
      const channelName = resize.channel_size?.channel?.name?.trim();
      // Render aspect-ratio labels as "1x1" rather than the reserved ":".
      const sizeLabel = (resize.channel_size?.label ?? "variant").replace(
        /:/g,
        "x",
      );
      const variantKey = [channelName, sizeLabel].filter(Boolean).join("-");
      const variantName = `${baseName}-${variantKey}`;
      try {
        await pushRenderedEntries({
          content: resize.svg,
          entryName: variantName,
          raster: resize.version_id
            ? { assetId: resize.id, target: { versionId: resize.version_id } }
            : null,
          componentAssets:
            (resize.component_assets as Record<string, string> | null) ??
            primaryComponentAssets,
        });
      } catch (err) {
        pushPrepFailure(variantName, err);
      }
    }

    return { entries, warnings, pdfPages };
  }

  // The markup branch above returned, so a video URL here is an uploaded video file.
  const isVideoFile = isVideoAsset(asset.asset_url);
  if (isVideoFile && formats.includes("pdf")) {
    warnings.push(`${displayName} is a video, so it isn't in the PDF.`);
  }
  // No editable markup — download the stored file directly (uploaded images,
  // rendered videos). These have no HTML5 bundle representation. A PDF-only
  // export skips fetching a video it can't use.
  if (
    asset.asset_url &&
    !(isVideoFile && formats.every((format) => format === "pdf"))
  ) {
    const blob = await fetchUrlAsBlob(asset.asset_url);
    if (blob) {
      const contentType = blob.type || asset.asset_type || "image/png";
      // A PDF-only export asked for the review document, not the stored files.
      if (formats.some((format) => format !== "pdf")) {
        entries.push({
          filename: sanitizeFilename(resolveFilename(baseName, contentType)),
          blob,
        });
      }
      if (formats.includes("pdf") && asset.asset_type === "image") {
        pdfPages.push({
          name: baseName,
          label: displayName,
          image: blob,
          machineReadableDisclosure: false,
        });
      }
      // The stored file is the served ad; videos aren't matchable.
      if (!contentType.startsWith("video") && primaryRasterTarget) {
        void requestAdRaster({
          apiEndpoint: apiBase,
          assetId: asset.id,
          target: primaryRasterTarget,
        });
      }
    }
  }

  return { entries, warnings, pdfPages };
}

/** Toast for an empty download: a warning is the reason, so it outranks the generic message. */
export function emptyDownloadToast(args: {
  idPrefix: string;
  warnings: string[];
  emptyTitle: string;
  emptyMessage?: string;
}): {
  id: string;
  title: string;
  message?: string;
  variant: "error" | "warning";
} {
  if (args.warnings.length > 0) {
    return {
      id: `${args.idPrefix}-failed`,
      title: "Couldn't prepare the download",
      message: args.warnings.join(" "),
      variant: "error",
    };
  }
  return {
    id: `${args.idPrefix}-empty`,
    title: args.emptyTitle,
    message: args.emptyMessage,
    variant: "warning",
  };
}

/**
 * Full records for grid-selected ads, a few requests at a time: the grids carry
 * only the card fields. Ids that fail to load are returned as errors, not thrown.
 */
export async function fetchAdBriefAssetsSettled(assetIds: string[]): Promise<{
  assets: DownloadedAdAsset[];
  errors: unknown[];
}> {
  const limit = pLimit(PARALLEL_RECORD_FETCHES);
  const results = await Promise.allSettled(
    assetIds.map((assetId) =>
      limit(() => useAdBriefAssetQuery.fetcher({ assetId })()),
    ),
  );
  const assets: DownloadedAdAsset[] = [];
  const errors: unknown[] = [];
  for (const result of results) {
    if (result.status === "rejected") {
      errors.push(result.reason);
    } else if (result.value.ad_brief_assets_by_pk) {
      assets.push(result.value.ad_brief_assets_by_pk);
    }
  }
  return { assets, errors };
}

/** Every record, or a throw: for callers that must not continue with part of a selection. */
export async function fetchAdBriefAssets(
  assetIds: string[],
): Promise<DownloadedAdAsset[]> {
  const { assets, errors } = await fetchAdBriefAssetsSettled(assetIds);
  if (errors.length > 0) throw errors[0];
  return assets;
}

function brandProfileIdOf(asset: DownloadedAdAsset): string | null {
  return asset.ad_brief?.ad_studio_project?.brand_profile_id ?? null;
}

function assetLabel(asset: DownloadedAdAsset): string {
  return adDisplayName({ assetName: asset.asset_name });
}

/** A selection waiting on the export-settings dialog. */
type PendingDownload = {
  statics: DownloadedAdAsset[];
  videos: DownloadedAdAsset[];
  zipName: string;
  /** The one format the dialog offers; null offers every bulk format. */
  format: DownloadOption | null;
  /** Videos a single-format export leaves out, named in the dialog's banner. */
  leftOutVideoCount: number;
  /** Set when a static ad has markup, so formats and disclosures are configurable. */
  staticSettings: { showDisclosures: boolean } | null;
  /** Set when a video brand still wants a disclosure. */
  videoSettings: VideoExportSettings | null;
};

type PerformDownloadArgs = {
  assets: DownloadedAdAsset[];
  resultsByAssetId: Map<string, DisclaimerDownloadResult>;
  zipName: string;
  formats: AdAssetDownloadFormat[];
  imageFileType?: ImageFileType;
  pdfImageFileType?: ImageFileType;
  extras?: ExtraZipEntries;
};

type UseAdAssetDownloadResult = {
  /**
   * Downloads each ad's base image plus any created channel sizes (or its stored file for
   * non-SVG assets), and renders composition ads through the video export. Opens the
   * export-settings dialog first when there is anything to configure; the dialog closes on
   * confirm and the downloads run behind it. A single file downloads raw; multiple bundle
   * into `zipName`. An aborted `options.signal` drops the loaded ads before anything opens.
   */
  downloadAssets: (
    assetIds: string[],
    zipName?: string,
    options?: {
      /** Offers only this format and leaves videos out: every format renders static ads. */
      format?: DownloadOption;
      signal?: AbortSignal;
    },
  ) => Promise<void>;
  isDownloading: boolean;
  /** True while a run or its dialog owns the hook, so `downloadAssets` would ignore a new call. */
  isBusy: boolean;
  /** Render this alongside the triggering surface — hosts the settings and video progress dialogs. */
  downloadDialog: ReactNode;
  /** With `embedSettings`, the pending export's settings for the caller's own dialog to render. */
  settings: DownloadDialogSettings | undefined;
  /** With `embedSettings`, drops the pending export when the caller's dialog closes without confirming. */
  dismissSettings: () => void;
  /** With `embedSettings`, confirms the pending export and zips `extras` together with the ads. */
  confirmSettings: (
    result: ExportSettingsResult,
    extras?: ExtraZipEntries,
  ) => void;
};

export function useAdAssetDownload(
  options: {
    /** Base name for the video archive, without an extension. */
    videoFilename?: string;
    /** The caller renders the settings step through `settings`; `downloadDialog` then shows progress only. */
    embedSettings?: boolean;
  } = {},
): UseAdAssetDownloadResult {
  const { videoFilename = "ad-videos", embedSettings = false } = options;
  const { toast } = useToast();
  const { getFontRulesForMarkup } = useFontInjection();
  const labelColors = useReviewPdfLabelColors();
  const resolveFontFor = usePsdFontResolver();
  const { apiEndpoint } = useRegionConfig();
  const resolveBrandDefaults = useBrandDisclaimerDefaultsResolver();
  const {
    startVideos,
    isOverVideoRenderCap,
    staticTracker,
    progress,
    closeProgress,
  } = useDownloadProgress({ videoFilename });

  const [isDownloading, setIsDownloading] = useState(false);
  // Assets awaiting the export-settings dialog; non-null renders it.
  const [pending, setPending] = useState<PendingDownload | null>(null);

  const performDownload = useCallback(
    async ({
      assets: requestedAssets,
      resultsByAssetId,
      zipName,
      formats,
      imageFileType = PNG_IMAGE_FILE_TYPE,
      pdfImageFileType = DEFAULT_PDF_IMAGE_FILE_TYPE,
      extras,
    }: PerformDownloadArgs): Promise<void> => {
      const { items: assets, isOverCap } = capPdfExport(
        requestedAssets,
        formats,
      );
      if (isOverCap) toast(PDF_EXPORT_CAP_TOAST);
      setIsDownloading(true);
      staticTracker.begin(
        assets.map((asset) => ({ key: asset.id, name: assetLabel(asset) })),
      );
      try {
        const limit = pLimit(adDownloadConcurrency(formats));
        const resultsPerAsset = await Promise.all(
          assets.map((asset) =>
            limit(async (): Promise<AdAssetDownloadEntriesResult> => {
              // Cancelling can't drain pLimit's queue, and a PSD build costs a
              // full-size plus 2x canvas per layer, so each task checks first.
              if (staticTracker.isCancelled()) {
                return { entries: [], warnings: [], pdfPages: [] };
              }
              staticTracker.setItemStatus(asset.id, "running");
              try {
                const result = resultsByAssetId.get(asset.id);
                const built = await buildAdAssetDownloadEntries({
                  asset,
                  spec: result?.spec ?? DISABLED_DISCLAIMER_SPEC,
                  font: result?.font,
                  getFontRulesForMarkup,
                  loadFontResolver: () =>
                    resolveFontFor(brandProfileIdOf(asset)),
                  apiBase: apiEndpoint,
                  formats,
                  imageFileType,
                  primaryRasterTarget: assetVersionRasterTarget(asset),
                });
                staticTracker.setItemStatus(
                  asset.id,
                  built.entries.length > 0 || built.pdfPages.length > 0
                    ? "complete"
                    : "failed",
                );
                return built;
              } catch (err) {
                console.error(
                  `asset-download: ${assetLabel(asset)} failed`,
                  err,
                );
                staticTracker.setItemStatus(asset.id, "failed");
                return {
                  entries: [],
                  warnings: [
                    `${assetLabel(asset)} couldn't be prepared, so its files were skipped.`,
                  ],
                  pdfPages: [],
                };
              }
            }),
          ),
        );

        const entries = [
          ...resultsPerAsset.flatMap((r) => r.entries),
          ...(extras?.entries ?? []),
        ];
        const warnings = resultsPerAsset.flatMap((r) => r.warnings);
        if (staticTracker.isCancelled()) return;

        // A lone asset's files are named after the ad; bulk files use the
        // surface-provided name.
        const loneAssetName = sanitizeFilename(assets[0]?.asset_name || "ad");
        const baseName =
          assets.length === 1 ? loneAssetName : zipName.replace(/\.zip$/i, "");
        const pdf = await buildReviewPdfEntry({
          pages: resultsPerAsset.flatMap((r) => r.pdfPages),
          imageFileType: pdfImageFileType,
          labelColors,
          filename: baseName,
        });
        if (pdf.entry) entries.push(pdf.entry);
        warnings.push(...pdf.warnings);

        if (entries.length === 0) {
          staticTracker.fail(
            warnings.length > 0
              ? "Couldn't prepare the download."
              : "The selected ads have no downloadable content.",
            warnings,
          );
          return;
        }
        if (staticTracker.isCancelled()) return;

        staticTracker.zipping();
        await downloadBlobsAsZipOrSingle(
          entries,
          extras?.zipName ?? `${baseName}.zip`,
        );
        staticTracker.finish({ fileCount: entries.length, warnings });
      } catch (err) {
        console.error("asset-download: unexpected failure", err);
        staticTracker.fail("Failed to download ads.");
      } finally {
        setIsDownloading(false);
      }
    },
    [
      apiEndpoint,
      getFontRulesForMarkup,
      labelColors,
      resolveFontFor,
      staticTracker,
      toast,
    ],
  );

  const downloadAssets = useCallback<
    UseAdAssetDownloadResult["downloadAssets"]
  >(
    async (assetIds, zipName = "ads.zip", { format, signal } = {}) => {
      // `isDownloading` only spans the fetch, and a video-only export never
      // sets it again, so the open dialog is what says a run already owns it.
      if (assetIds.length === 0 || isDownloading || pending || progress) return;

      setIsDownloading(true);
      let assets: DownloadedAdAsset[];
      try {
        const loaded = await fetchAdBriefAssetsSettled(assetIds);
        assets = loaded.assets;
        if (loaded.errors.length > 0) {
          captureException(loaded.errors[0]);
          // One failed record shouldn't sink the rest; only a total failure stops the download.
          if (loaded.errors.length === assetIds.length) {
            toast({
              id: "ad-asset-download-error",
              title: "Failed to download ads",
              variant: "error",
            });
            return;
          }
          toast({
            id: "ad-asset-download-partial",
            title: "Some ads couldn't be loaded",
            message: `${loaded.errors.length} of ${assetIds.length} ads couldn't be loaded, so they're left out of the download.`,
            variant: "warning",
          });
        }
      } catch (err) {
        captureException(err);
        toast({
          id: "ad-asset-download-error",
          title: "Failed to download ads",
          variant: "error",
        });
        return;
      } finally {
        setIsDownloading(false);
      }
      if (signal?.aborted) return;

      if (assets.length === 0) {
        toast({
          id: "ad-asset-download-empty",
          title: "Nothing to download",
          message: "The selected ads have no downloadable content.",
          variant: "warning",
        });
        return;
      }

      // Composition ads need a server-side render, so they leave the static path here.
      const partitioned = partitionAssetsForDownload(assets);
      // A single format covers static ads only, so rendered and uploaded videos stay out.
      const statics = format
        ? partitioned.staticAssets.filter(
            (asset) => getAdAssetKind(asset) !== "video-file",
          )
        : partitioned.staticAssets;
      const videos = format ? [] : partitioned.videoAssets;
      const leftOutVideoCount = assets.length - statics.length - videos.length;
      if (leftOutVideoCount > 0 && statics.length === 0) {
        toast({
          id: "single-format-videos-only",
          title: "Nothing to export",
          message: "The selected ads are all videos.",
          variant: "warning",
        });
        return;
      }

      // Bites before the settings dialog: over the cap, the whole download
      // stops rather than zipping the statics and dropping the videos.
      const renderCount = selectVideoExportTargets(videos).targets.length;
      if (isOverVideoRenderCap(renderCount)) return;
      const disclosuresDisabledFor = (batch: DownloadedAdAsset[]) =>
        batch.every((asset) =>
          areBrandDisclaimerDefaultsDisabled(
            resolveBrandDefaults(brandProfileIdOf(asset)),
          ),
        );

      // Only SVG-backed ads have formats and disclosures to configure; URL-only
      // assets download their stored files directly. Videos need the dialog
      // only while some brand still wants a disclosure.
      const staticSettings = statics.some((asset) => asset.svg)
        ? { showDisclosures: !disclosuresDisabledFor(statics) }
        : null;
      const videoSettings =
        videos.length > 0 && !disclosuresDisabledFor(videos)
          ? {
              count: videos.length,
              brandProfileIds: videos.map(brandProfileIdOf),
            }
          : null;

      // An embedding dialog downloads on its own confirm, so nothing starts before it.
      if (!staticSettings && !videoSettings && !embedSettings) {
        // No dialog opens to show the banner, so a toast says it instead.
        if (leftOutVideoCount > 0) {
          toast({
            id: "single-format-videos-left-out",
            ...SINGLE_FORMAT_VIDEOS_NOTICE,
            variant: "info",
          });
        }
        if (videos.length > 0) {
          startVideos(videos, DISABLED_DISCLAIMER_SPEC);
        }
        if (statics.length > 0) {
          await performDownload({
            assets: statics,
            resultsByAssetId: new Map(),
            zipName,
            formats: format ? downloadOptionsToFormats([format]) : ["png"],
          });
        }
        return;
      }

      setPending({
        statics,
        videos,
        zipName,
        format: format ?? null,
        leftOutVideoCount,
        staticSettings,
        videoSettings,
      });
    },
    [
      embedSettings,
      isDownloading,
      isOverVideoRenderCap,
      pending,
      performDownload,
      progress,
      resolveBrandDefaults,
      startVideos,
      toast,
    ],
  );

  const handleConfirm = (
    result: ExportSettingsResult,
    extras?: ExtraZipEntries,
  ) => {
    if (!pending) return;
    setPending(null);
    // Renders take minutes, so they're requested before the zip builds.
    if (pending.videos.length > 0) {
      startVideos(
        pending.videos,
        result.videoAssets?.spec ?? DISABLED_DISCLAIMER_SPEC,
      );
    }
    if (pending.statics.length > 0) {
      const formats: AdAssetDownloadFormat[] = result.staticAssets
        ? downloadOptionsToFormats(result.staticAssets.options)
        : ["png"];
      void performDownload({
        assets: pending.statics,
        resultsByAssetId: new Map(
          (result.staticAssets?.disclosures ?? []).map((r) => [r.id, r]),
        ),
        zipName: pending.zipName,
        formats,
        imageFileType: result.staticAssets?.imageFileType,
        pdfImageFileType: result.staticAssets?.pdfImageFileType,
        extras,
      });
    } else if (extras) {
      // Video renders arrive as their own archive, so the other files zip on their own.
      downloadBlobsAsZipOrSingle(extras.entries, extras.zipName).catch(
        (err: unknown) => {
          console.error("asset-download: other files failed to zip", err);
          toast({
            id: "ad-asset-download-extras",
            title: "Couldn't download the other files",
            message: err instanceof Error ? err.message : undefined,
            variant: "error",
          });
        },
      );
    }
  };

  const previewItems = useMemo<DisclaimerPreviewItem[]>(
    () =>
      (pending?.statics ?? []).flatMap((asset) => {
        if (!asset.svg) return [];
        return [
          {
            id: asset.id,
            label: asset.asset_name ?? undefined,
            content: asset.svg,
            componentAssets:
              (asset.component_assets as Record<string, string> | null) ?? null,
            brandProfileId: brandProfileIdOf(asset),
          },
        ];
      }),
    [pending?.statics],
  );

  const settings: DownloadDialogSettings | undefined = pending
    ? {
        staticAssets: pending.staticSettings
          ? {
              formatOptions: pending.format
                ? [pending.format]
                : BULK_FORMAT_OPTIONS,
              notice:
                pending.leftOutVideoCount > 0
                  ? SINGLE_FORMAT_VIDEOS_NOTICE
                  : undefined,
              showDisclosures: pending.staticSettings.showDisclosures,
              previewItems,
              getFontRulesForMarkup,
              apiBase: apiEndpoint,
            }
          : undefined,
        videoAssets: pending.videoSettings ?? undefined,
        onConfirm: (result) => handleConfirm(result),
      }
    : undefined;
  const dismissSettings = useCallback(() => setPending(null), []);

  // One dialog element across both steps, so confirming swaps the settings
  // body for progress inside the same modal instead of remounting it.
  const downloadDialog =
    (pending && !embedSettings) || progress ? (
      <DownloadDialog
        isOpen
        onClose={progress ? closeProgress : dismissSettings}
        settings={embedSettings ? undefined : settings}
        progress={progress ?? undefined}
      />
    ) : null;

  return {
    downloadAssets,
    isDownloading,
    isBusy: isDownloading || pending != null || progress != null,
    downloadDialog,
    settings: embedSettings ? settings : undefined,
    dismissSettings,
    confirmSettings: handleConfirm,
  };
}
